import { useCallback } from 'react';

/**
 * Where the dismissal is remembered, and the attribute the stylesheet watches.
 * Both are also named in the pre-paint script in `root.tsx`; change one and
 * you must change all three.
 */
export const PROMISE_BANNER_KEY = 'kbx-promise-banner';
export const PROMISE_BANNER_ATTR = 'data-kbx-promise';

/**
 * Closing the import promise ribbon (§27b).
 *
 * **Visibility is CSS, not React state**, and that is the whole design. This
 * site is prerendered, so the HTML is written at build time and cannot know
 * whether *this* visitor has dismissed the ribbon. A `useState` seeded from
 * `sessionStorage` would therefore have to start at "visible" to match the
 * server, then flip after hydration — which is a red bar that appears and
 * vanishes, and 40px of layout jumping, on every hard load of a session the
 * visitor had already closed it in.
 *
 * Instead the pre-paint script in `root.tsx` puts an attribute on `<html>`
 * before the first frame, exactly as `InitColorSchemeScript` does for the
 * colour scheme, and the stylesheet hides the ribbon and zeroes its height
 * variable. React renders the same markup either way, so there is nothing to
 * mismatch and nothing to flash.
 *
 * `sessionStorage` and not `localStorage`: the ribbon is back on the next
 * visit. A first-time visitor — the one the message exists for — always sees
 * it, while someone who has read it and closed it is not asked to read it
 * again for the rest of that sitting.
 *
 * Every access is wrapped: `sessionStorage` throws outright in some privacy
 * modes, and a ribbon that cannot be closed is a far better failure than a
 * page that does not render.
 */
export function useImportPromiseBanner() {
  const dismiss = useCallback(() => {
    document.documentElement.setAttribute(PROMISE_BANNER_ATTR, 'dismissed');
    try {
      sessionStorage.setItem(PROMISE_BANNER_KEY, 'dismissed');
    } catch {
      // Private mode, or storage is blocked. The ribbon still closes for this
      // page view; it simply comes back on the next one.
    }
  }, []);

  return { dismiss };
}
